import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  ExamCenterProctorType,
  FilterOperator,
  useSearchAvailableExamCenterProctorsByTimeFrameQuery,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  Flex,
  FormRadioSelection,
  Text,
  type GridRowSelectionModel,
  SearchInput,
  Button,
  AddRoundedIcon,
  NoRowsOverlay,
  GroupOutlinedIcon,
} from '@shared/components';
import { WithPermission } from '@shared/permissions';

import { BaseAddProctorDetailsList } from '../../components';
import { useAddProctorSearchParams } from '../../hooks';
import { useProctoringDetailsModal } from '../../providers';
import { ProctorType } from '../add-proctor-modal';

import { columns } from './column';

interface AddProctorModalDetailsFormFieldsProps {
  totalSelectedProctors?: number;
  proctorTypes?: ProctorType;
  proctorIds?: string[];
  onSelectRow?: (selectedProctor: string[] | undefined) => void;
}

const proctorTypeMap = {
  PROCTOR_1: 'ผู้คุมสอบประเภทที่ 1',
  PROCTOR_2: 'ผู้คุมสอบประเภทที่ 2',
  PROCTOR_3: 'ผู้คุมสอบประเภทที่ 3',
};

export default function AddProctorModalDetailsFormFields(
  props: Readonly<AddProctorModalDetailsFormFieldsProps>,
) {
  const {
    totalSelectedProctors,
    proctorTypes = ProctorType.Proctor_1_And_Proctor_2,
    proctorIds,
    onSelectRow,
  } = props;
  const { setAction } = useProctoringDetailsModal();
  const [{ search, page, pageSize }, { changeSearch, changePageNumber, changePageSize }] =
    useAddProctorSearchParams();
  const { examEvent } = useExamEventDetails();
  const proctorTypeOptions = [
    {
      label: 'ผู้คุมสอบประเภทที่ 1 และ 2',
      value: ProctorType.Proctor_1_And_Proctor_2,
    },
    { label: 'ผู้คุมสอบประเภทที่ 3', value: ProctorType.Proctor_3 },
  ];

  const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedProctor = rowSelectionModel as string[];
    onSelectRow?.(selectedProctor);
  };

  const handleSearchInputChange = (value: string) => {
    changeSearch(value);
  };

  const proctorTypesArray =
    proctorTypes === ProctorType.Proctor_1_And_Proctor_2
      ? [ExamCenterProctorType.Proctor_1, ExamCenterProctorType.Proctor_2]
      : [ExamCenterProctorType.Proctor_3];

  const isSearching = search !== '';

  const { data: mainProctorResult, loading: isMainProctorResultLoading } =
    useSearchAvailableExamCenterProctorsByTimeFrameQuery({
      variables: {
        searchAvailableExamCenterProctorsByTimeFrameInput: {
          startedAt: examEvent.examStartedAt,
          endedAt: examEvent.examEndedAt,
          searchOptions: {
            filter: {
              searchText: {
                fields: ['fullName'],
                value: search,
              },
              conditions: [
                {
                  field: 'examCenterCode',
                  operator: FilterOperator.Equal,
                  value: examEvent.examCenterCode,
                },
                {
                  field: 'type',
                  value: proctorTypesArray.length > 0 ? proctorTypesArray : null,
                  operator: FilterOperator.In,
                },
              ],
            },
            paging: {
              currentPage: page,
              pageSize: pageSize,
            },
          },
        },
      },
      fetchPolicy: 'network-only',
    });

  const proctors =
    mainProctorResult?.ecSearchAvailableExamCenterProctorsByTimeFrame.examCenterProctors || [];
  const total = mainProctorResult?.ecSearchAvailableExamCenterProctorsByTimeFrame.total || 0;
  const rows = proctors.map((proctor) => ({
    id: proctor.id,
    fullName: [proctor.titleTh, proctor.firstNameTh, proctor.middleNameTh, proctor.lastNameTh]
      .filter(Boolean)
      .join(' '),
    type: proctorTypeMap[proctor.type],
  }));

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          backgroundColor: theme.palette.grey[50],
          borderRadius: theme.shape.rounded(2),
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(4),
          gap: theme.spacing(4),
        })}
      >
        <Text variant='body1Semibold'>ประเภทผู้คุมสอบ</Text>
        <FormRadioSelection name='proctorType' options={proctorTypeOptions} row />
      </Flex>
      <Flex sx={{ justifyContent: 'space-between' }}>
        <Text variant='body1Semibold'>เลือกผู้คุมสอบ ({totalSelectedProctors})</Text>
        {proctorTypes === ProctorType.Proctor_3 ? (
          <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.CREATE}>
            <Button
              variant='outlined'
              startIcon={<AddRoundedIcon />}
              onClick={() => setAction({ action: 'create-proctor-third' })}
            >
              เพิ่มผู้คุมสอบประเภทที่ 3
            </Button>
          </WithPermission>
        ) : null}
      </Flex>
      <SearchInput placeholder='ค้นหาชื่อ' onChange={handleSearchInputChange} />
      <BaseAddProctorDetailsList
        columns={columns}
        rows={rows}
        loading={isMainProctorResultLoading}
        rowSelectionModel={proctorIds}
        onRowSelectionModelChange={handleRowSelectionChange}
        slots={{
          noRowsOverlay: () => {
            if (isSearching) {
              return <NoRowsOverlay title='ไม่พบข้อมูลที่ตรงกับการค้นหา' />;
            }
            return <NoRowsOverlay title='ยังไม่มีข้อมูลผู้คุมสอบ' icon={GroupOutlinedIcon} />;
          },
        }}
        slotProps={{
          footer: {
            total: total,
            pagination: {
              page: page,
              count: total,
              rowsPerPage: pageSize,
              onPageChange: (_event, page) => {
                changePageNumber(page);
              },
              onRowsPerPageChange: (event) => {
                const pageSize = parseInt(event.target.value);
                changePageSize(pageSize);
              },
            },
          },
        }}
      />
    </Flex>
  );
}
